/* AllySabers R7.11
   1. Saber Finder v2: four questions → a core with a reason, and three sabers priced and linked in that build.
      Loaded before ally-ignition.js, so this <ig-finder> definition is the one the browser uses.
   2. "Deflect the bolts": an arcade round layered on the Ignite demo (the blade lab in the core lab section), with
      waves, lives and a different power for each core. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CORES = ['base', 'xeno', 'proffie'];
  const NAMES = { base: 'Base-Lit', xeno: 'Xenopixel', proffie: 'Proffie' };
  const STAR_PATH = 'M10 1.6l2.5 5.2 5.7.8-4.1 4 1 5.6L10 14.5l-5.1 2.7 1-5.6-4.1-4 5.7-.8zm20 0l2.5 5.2 5.7.8-4.1 4 1 5.6L30 14.5l-5.1 2.7 1-5.6-4.1-4 5.7-.8zm20 0l2.5 5.2 5.7.8-4.1 4 1 5.6L50 14.5l-5.1 2.7 1-5.6-4.1-4 5.7-.8zm20 0l2.5 5.2 5.7.8-4.1 4 1 5.6L70 14.5l-5.1 2.7 1-5.6-4.1-4 5.7-.8zm20 0l2.5 5.2 5.7.8-4.1 4 1 5.6L90 14.5l-5.1 2.7 1-5.6-4.1-4 5.7-.8z';

  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const money = cents => {
    try {
      const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      const whole = cents % 100 === 0;
      return new Intl.NumberFormat(root.lang || 'en', { style: 'currency', currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const withVariant = (url, id) => (id ? `${url}${url.indexOf('?') > -1 ? '&' : '?'}variant=${id}` : url);

  /* ------------------------------------------------------------ */
  /* 1. Saber Finder                                              */
  /* ------------------------------------------------------------ */

  // How strongly each answer points at each core. Ties go to Xenopixel, then Base-Lit.
  const WEIGHTS = {
    who: {
      first: { base: 1, xeno: 2 },
      collector: { xeno: 1, proffie: 3 },
      gift: { base: 1, xeno: 2 },
      kid: { base: 4 }
    },
    use: {
      duel: { base: 3, xeno: 1 },
      show: { xeno: 3, proffie: 2 },
      display: { xeno: 2, proffie: 1 },
      mix: { base: 1, xeno: 2 }
    }
  };
  const TIE = ['xeno', 'base', 'proffie'];

  const REASONS = {
    base: {
      duel: "You'll duel and spin, and Base-Lit is built for it: one LED in the hilt lights the whole blade, so there's nothing in the blade to break.",
      kid: 'Simple controls, a tough blade and the friendliest price. Base-Lit is the right first saber for a young Jedi.'
    },
    xeno: {
      show: 'You want it to look alive. Xenopixel lights the blade pixel by pixel, with animated ignition and clash flashes right where you hit.',
      display: 'On a stand or in your hand, Xenopixel is the one that turns heads: full-blade light, animated ignition and app control.',
      gift: 'Xenopixel is the one that makes people say wow: full-blade light, cinematic effects and controls anyone can pick up.',
      first: 'For a first saber, Xenopixel gives you the full cinematic blade without a learning curve.'
    },
    proffie: {
      collector: 'You already know sabers, so Proffie gives you full control: your own sound fonts, custom blade styles and every effect tuned.',
      show: 'For the biggest show, Proffie runs the most advanced effects and blade styles, and you can program your own.'
    }
  };

  const LABELS = {
    who: { first: 'First saber', collector: 'Collector', gift: 'Gift', kid: 'Young Jedi' },
    use: { duel: 'Duel & spin', show: 'Light up the room', display: 'Display', mix: 'Bit of everything' },
    look: { films: 'Film-inspired', original: 'Original design', any: 'Any look' }
  };

  class IgFinderV2 extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.console = this.querySelector('.ig-finder__console');
      this.steps = [...this.querySelectorAll('.ig-finder__step')];
      this.result = this.querySelector('[data-finder-result]');
      this.grid = this.querySelector('[data-finder-grid]');
      this.bars = [...this.querySelectorAll('.ig-finder__progress i')];
      this.count = this.querySelector('[data-finder-count]');
      this.backBtn = this.querySelector('[data-finder-back]');
      this.resetBtn = this.querySelector('[data-finder-reset]');
      this.answers = {};
      try { this.copy = JSON.parse(this.querySelector('script[data-finder-copy]').textContent); } catch (e) { this.copy = { why: {}, urls: {} }; }
      try { this.items = JSON.parse(this.querySelector('script[data-finder-data]').textContent) || []; } catch (e) { this.items = []; }
      // one entry per saber (it can be in both collections)
      const seen = new Set();
      this.items = this.items.filter(it => it && it.h && !seen.has(it.h) && seen.add(it.h)).map((it, i) => Object.assign(it, { o: i }));

      this.steps.forEach((step, i) => {
        step.querySelectorAll('.ig-finder__opt').forEach(opt => {
          opt.setAttribute('aria-pressed', 'false');
          opt.addEventListener('click', () => this.answer(i, step.dataset.q, opt));
        });
      });
      this.backBtn?.addEventListener('click', () => this.show(Math.max(0, this.step - 1)));
      // inside the pop-up sheet, a link to a section on this page closes the sheet first
      this.addEventListener('click', e => {
        const a = e.target.closest('a[href*="#"]');
        const sheet = this.closest('.ig-quiz');
        if (!a || !sheet || a.pathname !== location.pathname) return;
        sheet.querySelector('.ig-quiz__close')?.click();
      });
      const reset = () => {
        this.answers = {};
        this.querySelectorAll('.ig-finder__opt').forEach(o => o.setAttribute('aria-pressed', 'false'));
        this.show(0);
      };
      this.resetBtn?.addEventListener('click', reset);
      this.querySelector('[data-finder-retake]')?.addEventListener('click', reset);

      // A–D on the keyboard picks an answer on the current question
      this.addEventListener('keydown', e => {
        if (e.metaKey || e.ctrlKey || e.altKey || this.step >= this.steps.length) return;
        if (/^(input|textarea|select)$/i.test(e.target.tagName)) return;
        const k = e.key && e.key.toUpperCase();
        const opts = [...this.steps[this.step].querySelectorAll('.ig-finder__opt')];
        const idx = ['A', 'B', 'C', 'D'].indexOf(k);
        if (idx > -1 && opts[idx]) { e.preventDefault(); opts[idx].click(); }
      });

      this.show(0, false);
    }

    answer(i, q, opt) {
      opt.parentElement.querySelectorAll('.ig-finder__opt').forEach(o => o.setAttribute('aria-pressed', o === opt ? 'true' : 'false'));
      this.answers[q] = opt.dataset.value;
      this.answers[`${q}Label`] = opt.querySelector('strong')?.textContent.trim();
      this.answers[`${q}Min`] = opt.dataset.min || '';
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.show(i + 1), reduceMotion.matches ? 0 : 260);
    }

    show(n, focus = true) {
      const total = this.steps.length;
      const done = n >= total;
      this.step = n;
      this.steps.forEach((s, k) => (s.hidden = k !== n));
      this.result.hidden = !done;
      this.console.dataset.step = n;
      this.bars.forEach((b, k) => b.classList.toggle('is-on', k <= n));
      this.count.textContent = done ? 'Your match' : `Step ${n + 1} of ${total}`;
      if (this.backBtn) this.backBtn.hidden = n === 0;
      if (this.resetBtn) this.resetBtn.hidden = n === 0;
      if (done) this.render();
      if (!focus) return;

      const target = done ? this.querySelector('[data-finder-focus]') : this.steps[n].querySelector('[aria-pressed="true"]') || this.steps[n].querySelector('.ig-finder__opt');
      target?.focus({ preventScroll: true });

      // keep the console in view when its height changes (mostly on phones)
      const top = this.console.getBoundingClientRect().top;
      const behavior = reduceMotion.matches ? 'auto' : 'smooth';
      const sheet = this.closest('.ig-quiz__body');
      if (sheet) {
        const edge = sheet.getBoundingClientRect().top;
        if (top < edge || done) sheet.scrollBy({ top: top - edge - 12, behavior });
        return;
      }
      const header = parseFloat(getComputedStyle(root).getPropertyValue('--header-height')) || 72;
      if (top < header || done) window.scrollBy({ top: top - header - 12, behavior });
    }

    pickCore() {
      const s = { base: 0, xeno: 0, proffie: 0 };
      const add = w => w && Object.keys(w).forEach(k => (s[k] += w[k]));
      add(WEIGHTS.who[this.answers.who]);
      add(WEIGHTS.use[this.answers.use]);
      const limit = parseInt(this.answers.budget, 10);
      if (limit > 0 && limit <= (this.copy.low || 15000)) s.base += 2;
      else if (this.answers.budget === '0') { s.proffie += 1; s.xeno += 1; }
      else if (limit > 0) s.xeno += 1;
      return TIE.slice().sort((a, b) => s[b] - s[a] || TIE.indexOf(a) - TIE.indexOf(b))[0];
    }

    /* Up to `want` sabers with a `core` build inside the budget, best first. */
    find(core, { strictLook = true, ignoreBudget = false, ignoreMin = false, want = 3, seen = new Set() } = {}) {
      const { look, who } = this.answers;
      const limit = parseInt(this.answers.budget, 10) || 0; // 0 = no cap ("and up"), -1 = flexible
      const min = ignoreBudget || ignoreMin ? 0 : parseInt(this.answers.budgetMin, 10) || 0;
      const cap = ignoreBudget || limit <= 0 ? Infinity : limit;
      const preferEco = (who === 'kid' || who === 'first') && limit > 0 && limit <= (this.copy.low || 15000);

      return this.items
        .filter(it => {
          if (seen.has(it.h)) return false;
          const p = it.c && it.c[core] && it.c[core][0];
          if (!p || p > cap || p < min) return false;
          if (strictLook && look === 'films' && !it.rep) return false;
          if (strictLook && look === 'original' && it.rep) return false;
          return true;
        })
        .map(it => {
          let rank = it.o;
          if (it.n >= 5 && it.r >= 4.6) rank -= 6;
          if (it.n >= 5 && it.r > 0 && it.r < 4) rank += 40;
          if (preferEco && it.eco) rank -= 20;
          return { it, rank, price: it.c[core][0], variant: it.c[core][1], core };
        })
        .sort((a, b) => a.rank - b.rank)
        .slice(0, want);
    }

    startsAt(core) {
      let best = 0;
      this.items.forEach(it => {
        const p = it.c && it.c[core] && it.c[core][0];
        if (p && (!best || p < best)) best = p;
      });
      return best;
    }

    render() {
      const { who, use, look, budget } = this.answers;
      const wanted = this.pickCore();
      let core = wanted;
      let note = '';
      const seen = new Set();
      const take = list => list.forEach(m => seen.add(m.it.h));

      // 1. the recommended core, in the chosen look and budget
      let matches = this.find(core, { seen });
      take(matches);
      // 2. a middle or top budget step: allow cheaper builds of the same core
      if (matches.length < 3 && parseInt(this.answers.budgetMin, 10) > 0) {
        const more = this.find(core, { ignoreMin: true, want: 3 - matches.length, seen });
        take(more);
        matches = matches.concat(more);
      }
      // 3. fill from the other look before giving up on the look entirely
      if (matches.length < 3 && look !== 'any') {
        const more = this.find(core, { strictLook: false, ignoreMin: true, want: 3 - matches.length, seen });
        take(more);
        matches = matches.concat(more);
      }
      // 4. a capped budget with only one or two builds of that core: add the best builds of the core below
      let extra = null;
      if (matches.length && matches.length < 3 && parseInt(budget, 10) > 0) {
        const n = matches.length;
        for (const c of CORES.slice(0, CORES.indexOf(core)).reverse()) {
          let more = this.find(c, { ignoreMin: true, want: 3 - matches.length, seen });
          take(more);
          if (matches.length + more.length < 3) {
            const wider = this.find(c, { strictLook: false, ignoreMin: true, want: 3 - matches.length - more.length, seen });
            take(wider);
            more = more.concat(wider);
          }
          if (more.length && !extra) extra = { core: c, n };
          matches = matches.concat(more);
          if (matches.length >= 3) break;
        }
        if (extra) note = `Only ${n === 1 ? 'one' : 'two'} ${NAMES[core]} build${n === 1 ? ' fits' : 's fit'} your budget, so we added the best ${NAMES[extra.core]} builds too.`;
      }
      // 5. nothing with that core in budget: step down a core
      if (!matches.length) {
        const lower = CORES.slice(0, CORES.indexOf(core)).reverse();
        for (const c of lower) {
          matches = this.find(c, { ignoreMin: true });
          if (matches.length < 3) matches = matches.concat(this.find(c, { strictLook: false, ignoreMin: true, want: 3 - matches.length, seen: new Set(matches.map(m => m.it.h)) }));
          if (matches.length) {
            core = c;
            const from = this.startsAt(wanted);
            note = from ? `${NAMES[wanted]} builds start at ${money(from)}, so these are the closest matches in your budget.` : '';
            break;
          }
        }
      }
      // 6. still nothing: the most affordable builds of the recommended core
      if (!matches.length) {
        matches = this.find(wanted, { strictLook: false, ignoreBudget: true, want: 30 }).sort((a, b) => a.price - b.price).slice(0, 3);
        core = wanted;
        if (matches.length && parseInt(budget, 10) > 0) note = 'Nothing fits that budget right now, so these are the most affordable matches.';
      }

      // heading + reason
      this.querySelector('[data-finder-core]').textContent = NAMES[core];
      this.querySelector('[data-finder-level]').dataset.level = CORES.indexOf(core) + 1;
      const wrap = this.querySelector('[data-finder-core-wrap]');
      if (wrap) wrap.dataset.core = core;

      const reason = (REASONS[core] && (REASONS[core][use] || REASONS[core][who])) || (this.copy.why && this.copy.why[core]) || '';
      // only name the look when every pick has it
      const onLook = look === 'any' || matches.every(m => (look === 'films') === !!m.it.rep);
      const lookWords = (onLook && { films: 'film-inspired hilts', original: 'original AllySabers designs' }[look]) || 'sabers';
      const count = matches.length;
      const inBudget = parseInt(budget, 10) > 0 && !note ? ' in your budget' : '';
      let second = count ? ` Below: ${count === 1 ? 'our top pick' : `${count === 2 ? 'two' : 'three'} ${lookWords}`} with a ${NAMES[core]} build${inBudget}, priced as built.` : '';
      if (extra) second = ' Below: the best builds in your budget, priced as built.';
      this.querySelector('[data-finder-why]').textContent = reason + second;
      const noteEl = this.querySelector('[data-finder-note]');
      noteEl.textContent = note;
      noteEl.hidden = !note;

      const chips = [LABELS.who[who], LABELS.use[use], LABELS.look[look], this.answers.budgetLabel].filter(Boolean);
      this.querySelector('[data-finder-answers]').replaceChildren(...chips.map(t => Object.assign(document.createElement('li'), { textContent: t })));

      // actions
      const top = this.querySelector('[data-finder-top]');
      const all = this.querySelector('[data-finder-all]');
      const urls = this.copy.urls || {};
      if (top) {
        top.hidden = !count;
        if (count) top.href = withVariant(matches[0].it.u, matches[0].variant);
      }
      if (all) {
        all.href = (look === 'films' ? urls.films : urls.all) || all.href;
        all.firstChild.nodeValue = look === 'films' ? 'All replica sabers ' : 'All sabers ';
      }

      const picksTitle = this.querySelector('[data-finder-picks-title]');
      if (picksTitle) {
        const base = this.copy.picks || 'Our top 3 for you';
        picksTitle.hidden = !count;
        picksTitle.textContent = count === 1 ? base.replace(/top 3/i, 'top pick') : base.replace(/\b3\b/, String(count || 3));
      }

      if (count) {
        this.grid.replaceChildren(...matches.map((m, i) => this.card(m, i)));
      } else {
        const empty = Object.assign(document.createElement('p'), { className: 'ig-finder__empty', textContent: 'We could not find a match right now. Browse the full range instead.' });
        this.grid.replaceChildren(empty);
      }
      this.dispatchEvent(new CustomEvent('finder:result', { bubbles: true, detail: { core, answers: { ...this.answers }, picks: matches.map(m => m.it.h) } }));
    }

    card(m, i) {
      const it = m.it;
      const href = withVariant(it.u, m.variant);
      const cores = CORES.filter(c => it.c[c] && it.c[c][0]).map(c => `<span data-core="${c}">${NAMES[c]}</span>`).join('');
      const fill = it.r ? Math.round((it.r / 5) * 1000) / 10 : 0;
      const rating = it.n >= 3 && it.r
        ? `<span class="ally-rating ally-rating--card"><span class="ally-stars" style="--ally-fill: ${fill}%" aria-hidden="true"><svg class="ally-stars__base" viewBox="0 0 100 20" focusable="false"><path d="${STAR_PATH}"/></svg><span class="ally-stars__fill"><svg viewBox="0 0 100 20" focusable="false"><path d="${STAR_PATH}"/></svg></span></span><span class="ally-rating__value" aria-hidden="true">${it.r.toFixed(1)}</span><span class="ally-rating__count" aria-hidden="true">(${it.n})</span><span class="sr-only">Rated ${it.r.toFixed(1)} out of 5, ${it.n} reviews</span></span>`
        : '';
      const wrap = document.createElement('div');
      wrap.className = 'ig-finder__item';
      wrap.setAttribute('role', 'listitem');
      wrap.style.setProperty('--i', i);
      wrap.dataset.corePick = m.core;
      wrap.innerHTML = `<a href="${esc(href)}" class="ig-card ig-qz-card">`
        + `<div class="ig-card__media"><img src="${esc(it.i)}" alt="${esc(it.t)}" loading="lazy" width="600" height="600">`
        + (it.i2 ? `<img src="${esc(it.i2)}" alt="" loading="lazy" width="600" height="600">` : '')
        + (i === 0 ? '<span class="ig-card__badge ig-qz-badge">Best match</span>' : '')
        + '</div>'
        + `<div class="ig-card__body">${cores ? `<span class="ig-card__cores">${cores}</span>` : ''}`
        + `<h3 class="ig-card__title">${esc(it.t)}</h3>`
        + `<div class="ig-card__meta"><span class="ig-card__price"><small>${NAMES[m.core]} build </small>${money(m.price)}</span>${rating}</div>`
        + '</div></a>';
      return wrap;
    }
  }

  if (!customElements.get('ig-finder')) customElements.define('ig-finder', IgFinderV2);

  /* ------------------------------------------------------------ */
  /* 2. Deflect the bolts (Ignite demo arcade game)               */
  /* ------------------------------------------------------------ */

  // Waves of blaster bolts fired by shooters on the right edge; a glow warns which one fires next. A missed bolt
  // costs a life (three to start) and each wave is longer and faster. A bolt sent back into a shooter knocks it out
  // for a few seconds. Every core plays a little differently, so a round is also a feel for the cores:
  //   Base-Lit   tough blade: one extra life
  //   Xenopixel  pixel flash: bolts you send back take out other bolts on the way
  //   Proffie    Force push: blocks charge the Force; tap it (or press F / Space) to throw every bolt back

  const BEST_KEY = 'ally-bolts-best-v2';
  const FORCE_NEED = 6;
  const POWERS = {
    base: { name: 'Tough blade', text: 'One extra life.', lives: 4 },
    xeno: { name: 'Pixel flash', text: 'Bolts you send back take out other bolts.', lives: 3 },
    proffie: { name: 'Force push', text: 'Blocks charge the Force. Use it to throw every bolt back.', lives: 3 }
  };
  const TRY_NEXT = ['proffie', 'xeno', 'base'];
  const readBest = () => { try { return parseInt(localStorage.getItem(BEST_KEY), 10) || 0; } catch (e) { return 0; } };
  const saveBest = v => { try { localStorage.setItem(BEST_KEY, String(v)); } catch (e) { /* private mode */ } };
  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const FONT = '"Barlow Condensed", "Arial Narrow", sans-serif';

  const segDist = (px, py, ax, ay, bx, by) => {
    const vx = bx - ax, vy = by - ay;
    const len2 = vx * vx + vy * vy || 1;
    const t = clamp(((px - ax) * vx + (py - ay) * vy) / len2, 0, 1);
    const dx = ax + vx * t - px, dy = ay + vy * t - py;
    return { d: Math.hypot(dx, dy), t };
  };

  class BoltGame {
    constructor(lab) {
      this.lab = lab;
      this.stage = lab.stage;
      this.state = 'idle';
      this.best = readBest();
      this.played = new Set();
      this.build();
      this.loop = this.loop.bind(this);
    }

    build() {
      const st = this.stage;
      const stop = e => e.stopPropagation();
      this.canvas = Object.assign(document.createElement('canvas'), { className: 'ig-game__canvas' });
      this.canvas.setAttribute('aria-hidden', 'true');
      this.ctx = this.canvas.getContext('2d');

      this.flashEl = Object.assign(document.createElement('div'), { className: 'ig-game__flash' });
      this.flashEl.setAttribute('aria-hidden', 'true');

      this.hud = document.createElement('div');
      this.hud.className = 'ig-game__hud';
      this.hud.hidden = true;
      this.hud.innerHTML = '<span class="ig-game__score"><b data-g-score>0</b><small data-g-mult></small></span>'
        + '<span class="ig-game__meta"><span data-g-wave>Wave 1</span><span class="ig-game__lives" data-g-lives></span></span>';

      this.banner = document.createElement('p');
      this.banner.className = 'ig-game__banner';
      this.banner.setAttribute('aria-live', 'polite');

      this.forceBtn = document.createElement('button');
      this.forceBtn.type = 'button';
      this.forceBtn.className = 'ig-game__force';
      this.forceBtn.hidden = true;
      this.forceBtn.innerHTML = '<span>Force push</span><span class="ig-game__force-meter" aria-hidden="true"><i></i></span>';

      this.startBtn = document.createElement('button');
      this.startBtn.type = 'button';
      this.startBtn.className = 'ig-game__start';
      this.startBtn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor"/></svg><span>Play: deflect the bolts</span>';

      this.overlay = document.createElement('div');
      this.overlay.className = 'ig-game__overlay';
      this.overlay.hidden = true;

      [this.startBtn, this.overlay, this.forceBtn].forEach(el => ['pointerdown', 'pointerup', 'pointermove'].forEach(t => el.addEventListener(t, stop)));
      this.startBtn.addEventListener('click', () => this.intro());
      this.forceBtn.addEventListener('click', () => this.push());
      this.overlay.addEventListener('click', e => {
        const b = e.target.closest('[data-g]');
        if (!b) return;
        const act = b.dataset.g;
        if (act === 'again') this.intro();
        if (act === 'close') this.idle();
        if (act === 'try') this.tryCore(b.dataset.core);
        if (act === 'build') {
          const info = document.querySelector('.product-info');
          if (info) { e.preventDefault(); this.idle(); info.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' }); }
        }
      });

      st.append(this.canvas, this.flashEl, this.hud, this.banner, this.forceBtn, this.startBtn, this.overlay);
      this.lab.classList.add('has-game');

      // touch: during a round the blade points at your finger (and the page doesn't scroll under it)
      const aimAt = e => {
        if (this.state !== 'play' || e.pointerType === 'mouse' || !this.lab.pivot) return;
        const r = st.getBoundingClientRect();
        const x = e.clientX - r.left, y = e.clientY - r.top;
        this.aimHold = clamp(Math.atan2(y - this.lab.pivot.y, Math.max(40, x - this.lab.pivot.x)), -1.25, 0.95);
        this.lab.aim = this.aimHold;
      };
      st.addEventListener('pointerdown', aimAt);
      st.addEventListener('pointermove', aimAt);

      // F or Space uses the Force during a Proffie round
      document.addEventListener('keydown', e => {
        if (this.state !== 'play' || this.core !== 'proffie' || e.repeat) return;
        if (/^(input|textarea|select)$/i.test(e.target.tagName) || e.target.isContentEditable) return;
        if (e.key === 'f' || e.key === 'F' || e.key === ' ') {
          e.preventDefault();
          this.push();
        }
      });
    }

    size() {
      const r = this.stage.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.W = r.width;
      this.H = r.height;
      this.canvas.width = Math.round(r.width * dpr);
      this.canvas.height = Math.round(r.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    isPdp() { return document.body.classList.contains('page-type-product') && !!document.querySelector('.product-info'); }

    /* ---- round flow ---- */

    intro() {
      this.size();
      const lab = this.lab;
      if (!lab.on) lab.ignite();
      lab.hint?.('');
      this.core = POWERS[lab.core] ? lab.core : 'xeno';
      this.played.add(this.core);
      const power = POWERS[this.core];
      this.touch = matchMedia('(hover: none)').matches;
      this.clearScene();
      this.hud.hidden = true;
      this.forceBtn.hidden = true;
      this.overlay.hidden = false;
      this.overlay.className = 'ig-game__overlay is-intro';
      this.overlay.innerHTML = '<p class="ig-game__kicker">Deflect the bolts</p>'
        + `<p class="ig-game__power" data-core="${this.core}"><b>${NAMES[this.core]} power · ${power.name}</b><span>${power.text}</span></p>`
        + `<p class="ig-game__how">${this.touch ? 'Touch and drag to aim the blade.' : 'Your blade follows the mouse.'} Stop the red bolts. Miss ${power.lives} and you're out.</p>`
        + '<p class="ig-game__count" data-g-count>3</p>';
      this.startBtn.hidden = true;
      this.state = 'intro';
      lab.classList.add('is-playing');
      let n = 3;
      const tick = () => {
        if (this.state !== 'intro') return;
        if (lab.core !== this.core) { this.intro(); return; } // a core card was picked: new power, new countdown
        n -= 1;
        const el = this.overlay.querySelector('[data-g-count]');
        if (n > 0) { if (el) el.textContent = n; this.countTimer = setTimeout(tick, 650); } else this.start();
      };
      clearTimeout(this.countTimer);
      this.countTimer = setTimeout(tick, reduceMotion.matches ? 300 : 650);
    }

    clearScene() {
      this.bolts = [];
      this.charges = [];
      this.sparks = [];
      this.rings = [];
      this.pops = [];
      this.muzzles = [];
      this.turrets = [];
    }

    start() {
      this.overlay.hidden = true;
      this.hud.hidden = false;
      this.state = 'play';
      this.clearScene();
      this.score = 0;
      this.streak = 0;
      this.bestStreak = 0;
      this.hits = 0;
      this.knocked = 0;
      this.chains = 0;
      this.misses = 0;
      this.lives = POWERS[this.core].lives;
      this.force = 0;
      this.forceBtn.hidden = this.core !== 'proffie';
      this.wave = 0;
      this.slow = 0;
      this.ending = 0;
      this.aimHold = null;
      this.renderLives();
      this.nextWave();
      this.last = performance.now();
      this.updateHud();
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(this.loop);
    }

    spec(w) {
      const k = Math.min(w - 1, 8);
      const narrow = this.W < 560;
      return {
        count: 5 + 2 * w,
        speed: narrow ? this.W * Math.min(0.42 + 0.05 * k, 0.82) : Math.min(this.W, 1100) * Math.min(0.34 + 0.045 * k, 0.7),
        gap: Math.max(0.36, 1.0 - 0.09 * k),
        charge: Math.max(0.28, 0.5 - 0.03 * k),
        double: clamp(0.12 * (w - 2), 0, 0.4)
      };
    }

    nextWave() {
      this.wave += 1;
      this.waveSpec = this.spec(this.wave);
      this.toFire = this.waveSpec.count;
      this.nextSpawn = 0.25;
      // shooters along the right edge: two to start, up to five
      const n = Math.min(5, 2 + Math.floor(this.wave / 2));
      this.turrets = Array.from({ length: n }, (x, i) => ({
        y: this.H * clamp(lerp(0.12, 0.88, (i + 0.5) / n) + (Math.random() - 0.5) * 0.08, 0.08, 0.92),
        stun: 0,
        heat: 0
      }));
      this.phase = 'banner';
      this.phaseT = 1.1;
      this.say(`Wave ${this.wave}`, this.wave === 1 ? `${NAMES[this.core]} · ${POWERS[this.core].name}` : `${this.waveSpec.count} bolts`);
    }

    releaseAim() {
      if (this.aimHold != null) this.lab.aim = null;
      this.aimHold = null;
    }

    idle() {
      this.state = 'idle';
      this.releaseAim();
      clearTimeout(this.countTimer);
      cancelAnimationFrame(this.raf);
      this.raf = null;
      this.overlay.hidden = true;
      this.hud.hidden = true;
      this.forceBtn.hidden = true;
      this.banner.classList.remove('is-on');
      this.startBtn.hidden = false;
      this.lab.classList.remove('is-playing');
      this.ctx.clearRect(0, 0, this.W || 0, this.H || 0);
    }

    end() {
      this.state = 'over';
      this.releaseAim();
      cancelAnimationFrame(this.raf);
      this.raf = null;
      this.hud.hidden = true;
      this.forceBtn.hidden = true;
      this.banner.classList.remove('is-on');
      const fresh = this.score > this.best;
      if (fresh) { this.best = this.score; saveBest(this.best); }
      const name = NAMES[this.core];
      const cta = this.isPdp()
        ? `<a href="#" class="ig-btn ig-btn--primary" data-g="build">Build yours with ${name}</a>`
        : `<a href="/collections/all-sabers" class="ig-btn ig-btn--primary" data-g="shop">Shop ${name} sabers</a>`;
      const next = TRY_NEXT.find(c => !this.played.has(c));
      const tryBtn = next ? `<button type="button" class="ig-game__again" data-g="try" data-core="${next}">Try ${NAMES[next]}: ${POWERS[next].name.toLowerCase()}</button>` : '';
      this.overlay.className = 'ig-game__overlay is-end';
      this.overlay.innerHTML = `<p class="ig-game__kicker">${fresh && this.score > 0 ? 'New best' : 'Game over'} · wave ${this.wave}</p>`
        + `<p class="ig-game__final"><b>${this.score}</b><small>points</small></p>`
        + `<p class="ig-game__stats">${this.hits} blocked · ${this.knocked} shooters hit${this.chains ? ` · ${this.chains} chained` : ''} · best streak ${this.bestStreak} · top score ${this.best}</p>`
        + `<div class="ig-game__actions">${cta}<button type="button" class="ig-game__again" data-g="again">Play again</button>${tryBtn}</div>`
        + '<button type="button" class="ig-game__close" data-g="close" aria-label="Close the game"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>';
      this.overlay.hidden = false;
      this.lab.classList.remove('is-playing');
      this.ctx.clearRect(0, 0, this.W, this.H);
      this.overlay.querySelector('[data-g="again"]').focus({ preventScroll: true });
    }

    tryCore(core) {
      if (!POWERS[core]) return;
      const host = this.lab.closest('ig-cores');
      if (host && host.pick) host.pick(core);
      else this.lab.setCore?.(core);
      this.intro();
    }

    /* ---- HUD ---- */

    say(text, sub) {
      this.banner.innerHTML = `${esc(text)}${sub ? `<small>${esc(sub)}</small>` : ''}`;
      this.banner.classList.remove('is-on');
      void this.banner.offsetWidth;
      this.banner.classList.add('is-on');
    }

    renderLives() {
      const total = POWERS[this.core].lives;
      const wrap = this.hud.querySelector('[data-g-lives]');
      if (wrap.children.length !== total) wrap.innerHTML = '<i></i>'.repeat(total);
      [...wrap.children].forEach((pip, i) => pip.classList.toggle('is-lost', i >= this.lives));
      wrap.setAttribute('aria-label', `${this.lives} lives left`);
    }

    updateHud() {
      const mult = this.mult();
      this.hud.querySelector('[data-g-score]').textContent = this.score;
      this.hud.querySelector('[data-g-mult]').textContent = mult > 1 ? `×${mult}` : '';
      this.hud.querySelector('[data-g-wave]').textContent = `Wave ${this.wave}`;
      if (this.core === 'proffie') {
        const ready = this.force >= FORCE_NEED;
        this.forceBtn.style.setProperty('--fill', `${Math.round((this.force / FORCE_NEED) * 100)}%`);
        if (this.forceBtn.dataset.ready !== String(ready)) {
          this.forceBtn.dataset.ready = String(ready);
          this.forceBtn.querySelector('span').textContent = ready ? `Force push · ${this.touch ? 'tap' : 'F'}` : 'Force push';
        }
      }
    }

    mult() { return 1 + Math.min(3, Math.floor(this.streak / 5)); }

    shake(strong) {
      if (reduceMotion.matches) return;
      const st = this.stage;
      st.classList.remove('ig-game--shake', 'ig-game--shake-lg');
      void st.offsetWidth;
      st.classList.add(strong ? 'ig-game--shake-lg' : 'ig-game--shake');
    }

    pop(x, y, text, color) {
      this.pops.push({ x, y, text, color: color || '#fff', life: 0.8 });
    }

    burst(x, y, n, color, force = 1) {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2, v = (90 + Math.random() * 240) * force;
        this.sparks.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 60, life: 0.35 + Math.random() * 0.35, color });
      }
    }

    /* ---- the game ---- */

    blade() {
      const L = this.lab;
      if (!L.pivot) return null;
      const c = Math.cos(L.ang), s = Math.sin(L.ang);
      const a = L.x0, b = L.x0 + L.len * clamp(L.level, 0, 1);
      return { ax: L.pivot.x + c * a, ay: L.pivot.y + s * a, bx: L.pivot.x + c * b, by: L.pivot.y + s * b, c, s };
    }

    // pick a point the blade can reach, then a spot on the right edge to fire at it from
    aimShot() {
      const L = this.lab;
      const W = this.W, H = this.H;
      const narrow = W < 560;
      const a = narrow ? lerp(-1.05, 0.45, Math.random()) : lerp(-0.85, 0.6, Math.random());
      const r = L.x0 + L.len * lerp(0.35, 0.95, Math.random());
      const tx = clamp(L.pivot.x + Math.cos(a) * r, W * 0.2, W * 0.95);
      const ty = clamp(L.pivot.y + Math.sin(a) * r, H * 0.08, H * 0.92);
      const ready = this.turrets.filter(t => t.stun <= 0 && !this.charges.some(c => c.turret === t));
      if (!ready.length) return false;
      const turret = ready[Math.floor(Math.random() * ready.length)];
      this.charges.push({ turret, y: turret.y, tx, ty, t: 0, dur: this.waveSpec.charge });
      return true;
    }

    fire(c) {
      const sx = this.W - 6;
      const speed = this.waveSpec.speed * (0.9 + Math.random() * 0.2);
      const dx = c.tx - sx, dy = c.ty - c.y, d = Math.hypot(dx, dy) || 1;
      this.bolts.push({ x: sx, y: c.y, px: sx, py: c.y, vx: (dx / d) * speed, vy: (dy / d) * speed, state: 'in', age: 0 });
      this.muzzles.push({ x: this.W - 2, y: c.y, life: 0.16 });
    }

    loop(now) {
      if (this.state !== 'play') return;
      this.raf = requestAnimationFrame(this.loop);
      const raw = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      // pause while the demo is off screen or the tab is hidden
      if (document.hidden || this.lab.hidden || !this.lab.visible) return;
      if (this.lab.core !== this.core) { this.intro(); return; } // core switched mid-round: start over with its power
      if (Math.abs(this.stage.getBoundingClientRect().width - this.W) > 1) this.size();

      // slow motion on big combos and on the final miss
      if (this.slow > 0) this.slow -= raw;
      const dt = raw * (this.slow > 0 || this.ending > 0 ? 0.4 : 1);
      if (this.ending > 0) {
        this.ending -= raw;
        if (this.ending <= 0) { this.end(); return; }
      }

      const L = this.lab;
      if (this.aimHold != null) L.aim = this.aimHold; // touch: the blade stays where the finger left it

      // waves: banner, then the shots, then a short breather once the last bolt is dealt with
      if (!this.ending) {
        if (this.phase === 'banner') {
          this.phaseT -= raw;
          if (this.phaseT <= 0) this.phase = 'fight';
        } else if (this.phase === 'fight') {
          this.nextSpawn -= dt;
          if (this.toFire > 0 && this.nextSpawn <= 0) {
            if (this.aimShot()) {
              this.toFire -= 1;
              if (this.toFire > 0 && Math.random() < this.waveSpec.double && this.aimShot()) this.toFire -= 1;
              this.nextSpawn = this.waveSpec.gap * (0.8 + Math.random() * 0.4);
            } else {
              this.nextSpawn = 0.2; // every shooter is knocked out or busy
            }
          }
          if (!this.toFire && !this.charges.length && !this.bolts.some(b => b.state === 'in')) {
            const bonus = 3 * this.wave;
            this.score += bonus;
            this.say(`Wave ${this.wave} clear`, `+${bonus} bonus`);
            this.phase = 'rest';
            this.phaseT = 1.2;
          }
        } else if (this.phase === 'rest') {
          this.phaseT -= raw;
          if (this.phaseT <= 0) this.nextWave();
        }
      }

      this.turrets.forEach(t => {
        if (t.stun > 0) t.stun -= dt;
        t.heat = Math.max(0, t.heat - dt * 3);
      });
      this.charges = this.charges.filter(c => {
        if (c.turret.stun > 0) return false; // knocked out while charging
        c.t += dt;
        if (c.t < c.dur) return true;
        this.fire(c);
        c.turret.heat = 1;
        return false;
      });

      const bl = this.blade();
      const hitR = Math.max(16, (L.bw || 6) * 1.5 + 10);
      const lit = L.on && L.level > 0.9 && bl;

      this.bolts.forEach(b => {
        if (b.state === 'gone') return;
        b.px = b.x; b.py = b.y;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.age += dt;
        if (b.state === 'in' && lit) {
          // check a few points along this frame's path so fast bolts can't slip through
          for (let k = 0; k <= 3; k++) {
            const x = lerp(b.px, b.x, k / 3), y = lerp(b.py, b.y, k / 3);
            const h = segDist(x, y, bl.ax, bl.ay, bl.bx, bl.by);
            if (h.d < hitR) { this.deflect(b, h.t, x, y, bl); break; }
          }
        }
        if (b.state === 'in' && (b.x < (L.pivot ? L.pivot.x - 24 : 0) || b.y < -30 || b.y > this.H + 30)) {
          b.state = 'gone';
          this.miss(b);
        } else if (b.state === 'out') {
          if (b.x > this.W + 4) {
            b.state = 'gone';
            const t = this.turrets.find(q => q.stun <= 0 && Math.abs(q.y - b.y) < 34);
            if (t) {
              t.stun = 4;
              this.knocked += 1;
              this.score += 3;
              this.pop(this.W - 40, clamp(t.y - 14, 24, this.H - 24), '+3 hit', '#ffd28a');
              this.muzzles.push({ x: this.W - 2, y: t.y, life: 0.3, hit: true });
              this.burst(this.W - 4, t.y, 14, '#ffb74a', 0.9);
            }
          } else if (b.y < -40 || b.y > this.H + 40 || b.x < -40) {
            b.state = 'gone';
          } else {
            const sp = Math.hypot(b.vx, b.vy);
            const cur = Math.atan2(b.vy, b.vx);
            const steer = (x, y, rate) => {
              let diff = Math.atan2(y - b.y, x - b.x) - cur;
              diff = Math.atan2(Math.sin(diff), Math.cos(diff));
              const turn = clamp(diff, -rate * dt, rate * dt);
              b.vx = Math.cos(cur + turn) * sp;
              b.vy = Math.sin(cur + turn) * sp;
            };
            // Xenopixel's pixel flash: a returned bolt bends toward the nearest incoming bolt and takes it out
            let near = null;
            if (this.core === 'xeno') {
              let nd = 260;
              this.bolts.forEach(o => {
                if (o.state !== 'in') return;
                const d = Math.hypot(o.x - b.x, o.y - b.y);
                if (d < nd) { nd = d; near = o; }
              });
            }
            if (near) {
              steer(near.x, near.y, 3.2);
            } else if (b.vx > 0) {
              // otherwise it drifts a little toward the closest shooter still firing
              let t = null, td = Infinity;
              this.turrets.forEach(q => {
                const d = Math.abs(q.y - b.y);
                if (q.stun <= 0 && d < td) { td = d; t = q; }
              });
              if (t && td < this.H * 0.35) steer(this.W + 4, t.y, 1.1);
            }
            if (this.core === 'xeno') {
              this.bolts.forEach(o => {
                if (o.state !== 'in' || Math.hypot(o.x - b.x, o.y - b.y) > 30) return;
                o.state = 'gone';
                this.chains += 1;
                this.score += 1;
                this.rings.push({ x: o.x, y: o.y, t: 0, color: '#fff' });
                this.burst(o.x, o.y, 10, null, 0.8);
                this.pop(o.x, o.y - 10, '+1 chain');
              });
            }
          }
        }
      });
      this.bolts = this.bolts.filter(b => b.state !== 'gone');

      this.sparks.forEach(p => { p.x += p.vx * dt; p.y += p.vy * dt; p.vy += 420 * dt; p.life -= dt; });
      this.sparks = this.sparks.filter(p => p.life > 0);
      this.rings.forEach(r => (r.t += dt / 0.35));
      this.rings = this.rings.filter(r => r.t < 1);
      this.pops.forEach(p => { p.y -= 34 * dt; p.life -= dt; });
      this.pops = this.pops.filter(p => p.life > 0);
      this.muzzles.forEach(m => (m.life -= dt));
      this.muzzles = this.muzzles.filter(m => m.life > 0);

      this.draw();
      this.updateHud();
    }

    deflect(b, t, x, y, bl) {
      const L = this.lab;
      // send it back off at an angle set by the blade: angle the blade well and it flies home to the shooters
      const sp = Math.hypot(b.vx, b.vy) * 1.3;
      const out = clamp(-bl.s * 1.5 + (Math.random() - 0.5) * 0.9, -1.25, 1.0) - 0.1;
      b.vx = Math.cos(out) * sp;
      b.vy = Math.sin(out) * sp;
      b.state = 'out';
      b.age = 0;
      b.x = x; b.y = y;
      this.hits += 1;
      this.streak += 1;
      this.bestStreak = Math.max(this.bestStreak, this.streak);
      const gain = this.mult();
      this.score += gain;
      if (this.core === 'proffie' && this.force < FORCE_NEED) {
        this.force += 1;
        if (this.force === FORCE_NEED) this.say('Force ready', this.touch ? 'Tap Force push' : 'Press F or tap Force push');
      }
      this.burst(x, y, 12);
      this.rings.push({ x, y, t: 0 });
      this.pop(x, y - 14, `+${gain}`);
      // the lab's own blaster effect where the bolt landed (flash, sound, a little kick)
      try {
        if (Array.isArray(L.events)) L.events.push({ type: 'blaster', pos: clamp(t, 0.05, 0.98), t0: L.time });
        L.spray?.(clamp(t, 0.05, 0.98), 8, 0.6);
        L.sound?.blaster?.();
        L.vel = (L.vel || 0) + (Math.random() < 0.5 ? -1 : 1) * 0.5;
      } catch (e) { /* the lab keeps running either way */ }
      if (this.streak % 10 === 0) {
        this.slow = reduceMotion.matches ? 0 : 0.9;
        this.say(`Combo ×${this.mult()}`, `${this.streak} in a row`);
      } else if (this.streak === 5) {
        this.say('×2 points', '5 in a row');
      }
    }

    miss(b) {
      this.misses += 1;
      if (this.streak >= 5) this.say('Streak lost');
      this.streak = 0;
      this.lives -= 1;
      this.renderLives();
      this.flashEl.classList.remove('is-on');
      void this.flashEl.offsetWidth;
      this.flashEl.classList.add('is-on');
      this.burst(clamp(b.x, 10, this.W - 10), clamp(b.y, 10, this.H - 10), 8, '#ff2a3a', 0.7);
      this.shake(this.lives <= 0);
      if (this.lives <= 0 && !this.ending) {
        this.ending = 0.9;
        this.say('Out of lives');
      }
    }

    push() {
      if (this.state !== 'play' || this.core !== 'proffie' || this.force < FORCE_NEED || this.ending) return;
      const L = this.lab;
      this.force = 0;
      try { L.trigger?.('force'); } catch (e) { /* effect only */ }
      const bl = this.blade();
      const cx = bl ? (bl.ax + bl.bx) / 2 : this.W * 0.3;
      const cy = bl ? (bl.ay + bl.by) / 2 : this.H * 0.5;
      let n = 0;
      this.bolts.forEach(b => {
        if (b.state !== 'in') return;
        const a = Math.atan2(b.y - cy, Math.max(30, b.x - cx));
        const sp = Math.hypot(b.vx, b.vy) * 1.4;
        b.vx = Math.cos(a) * sp;
        b.vy = Math.sin(a) * sp;
        b.state = 'out';
        b.age = 0;
        n += 1;
        this.rings.push({ x: b.x, y: b.y, t: 0 });
      });
      this.charges = [];
      this.score += n;
      this.say('Force push', n ? `${n} bolt${n === 1 ? '' : 's'} thrown back` : 'Clear skies');
      this.shake(true);
      this.updateHud();
    }

    draw() {
      const g = this.ctx;
      const W = this.W, H = this.H;
      g.clearRect(0, 0, W, H);
      const lab = getComputedStyle(this.lab).getPropertyValue('--lab').trim() || '#2f8fff';
      g.save();
      g.globalCompositeOperation = 'lighter';
      g.lineCap = 'round';

      // shooters on the right edge: a dim red emitter, white sparks while knocked out
      this.turrets.forEach(t => {
        if (t.stun > 0) {
          const f = 0.35 + Math.random() * 0.3;
          g.fillStyle = `rgba(255,255,255,${f * 0.5})`;
          g.fillRect(W - 3, t.y - 9, 3, 18);
          if (Math.random() < 0.25) this.sparks.push({ x: W - 3, y: t.y + (Math.random() - 0.5) * 14, vx: -40 - Math.random() * 90, vy: (Math.random() - 0.5) * 90, life: 0.25, color: '#ffd28a' });
          return;
        }
        const glow = 0.28 + t.heat * 0.6;
        const grad = g.createRadialGradient(W, t.y, 0, W, t.y, 16);
        grad.addColorStop(0, `rgba(255,70,80,${glow})`);
        grad.addColorStop(1, 'rgba(255,42,58,0)');
        g.fillStyle = grad;
        g.fillRect(W - 16, t.y - 16, 16, 32);
        g.fillStyle = `rgba(255,80,90,${0.45 + t.heat * 0.5})`;
        g.fillRect(W - 3, t.y - 9, 3, 18);
      });

      // charging shots: a red glow on the right edge where the next bolt comes from
      this.charges.forEach(c => {
        const p = clamp(c.t / c.dur, 0, 1);
        const r = 5 + p * 13;
        const grad = g.createRadialGradient(W, c.y, 0, W, c.y, r * 2.2);
        grad.addColorStop(0, `rgba(255,${120 + p * 100 | 0},${110 + p * 80 | 0},${0.35 + p * 0.6})`);
        grad.addColorStop(0.4, `rgba(255,42,58,${0.25 + p * 0.45})`);
        grad.addColorStop(1, 'rgba(255,42,58,0)');
        g.fillStyle = grad;
        g.fillRect(W - r * 2.2, c.y - r * 2.2, r * 2.2, r * 4.4);
        g.fillStyle = `rgba(255,120,128,${0.5 + p * 0.5})`;
        g.fillRect(W - 4, c.y - 9 - p * 6, 4, 18 + p * 12);
      });

      // muzzle flashes (red) and return hits (white-gold)
      this.muzzles.forEach(m => {
        const a = clamp(m.life / 0.2, 0, 1);
        const r = (m.hit ? 34 : 26) * (1.2 - a * 0.4);
        const grad = g.createRadialGradient(m.x, m.y, 0, m.x, m.y, r);
        grad.addColorStop(0, m.hit ? `rgba(255,244,214,${a})` : `rgba(255,220,220,${a})`);
        grad.addColorStop(0.35, m.hit ? `rgba(255,183,74,${0.7 * a})` : `rgba(255,42,58,${0.7 * a})`);
        grad.addColorStop(1, 'rgba(255,42,58,0)');
        g.fillStyle = grad;
        g.fillRect(m.x - r, m.y - r, r * 2, r * 2);
      });

      // bolts
      this.bolts.forEach(b => {
        const sp = Math.hypot(b.vx, b.vy) || 1;
        const len = (W < 560 ? 30 : 46) * (b.state === 'out' ? 0.85 : 1);
        const tx = b.x - (b.vx / sp) * len, ty = b.y - (b.vy / sp) * len;
        const col = b.state === 'out' ? lab : '#ff2a3a';
        g.globalAlpha = 1;
        g.strokeStyle = col;
        g.shadowColor = col;
        g.shadowBlur = 20;
        g.lineWidth = W < 560 ? 6 : 8;
        g.beginPath(); g.moveTo(tx, ty); g.lineTo(b.x, b.y); g.stroke();
        g.shadowBlur = 0;
        g.strokeStyle = '#fff';
        g.lineWidth = 2.4;
        g.beginPath(); g.moveTo(lerp(tx, b.x, 0.25), lerp(ty, b.y, 0.25)); g.lineTo(b.x, b.y); g.stroke();
      });

      // impact rings
      this.rings.forEach(r => {
        g.globalAlpha = 1 - r.t;
        g.strokeStyle = r.color || lab;
        g.shadowColor = lab;
        g.shadowBlur = 12;
        g.lineWidth = 2.5 * (1 - r.t) + 0.5;
        g.beginPath();
        g.arc(r.x, r.y, 6 + r.t * 30, 0, Math.PI * 2);
        g.stroke();
      });

      // sparks
      g.shadowBlur = 10;
      this.sparks.forEach(p => {
        g.globalAlpha = clamp(p.life * 2.5, 0, 1);
        g.fillStyle = p.color || '#fff';
        g.shadowColor = p.color || lab;
        g.fillRect(p.x - 1.3, p.y - 1.3, 2.6, 2.6);
      });
      g.restore();

      // floating points
      g.save();
      g.textAlign = 'center';
      g.font = `600 ${W < 560 ? 16 : 19}px ${FONT}`;
      this.pops.forEach(p => {
        g.globalAlpha = clamp(p.life / 0.5, 0, 1);
        g.fillStyle = p.color;
        g.shadowColor = lab;
        g.shadowBlur = 10;
        g.fillText(p.text.toUpperCase(), p.x, p.y);
      });
      g.restore();
    }
  }

  const attachGame = lab => {
    if (lab.game || !lab.stage || !lab.canvas) return;
    lab.game = new BoltGame(lab);
    // switching tabs or cores stops a round cleanly
    const host = lab.closest('ig-cores');
    host?.addEventListener('click', e => {
      if (lab.game.state !== 'idle' && e.target.closest('[data-view]:not([data-view="demo"])')) lab.game.idle();
    });
  };

  const initGames = () => document.querySelectorAll('ig-blade-lab').forEach(lab => {
    if (lab.ready) attachGame(lab);
    else setTimeout(() => lab.ready && attachGame(lab), 400);
  });

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('ig-blade-lab').then(() => {
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initGames);
      else initGames();
    });
  }
  document.addEventListener('shopify:section:load', () => setTimeout(initGames, 300));
})();
